import React, { useState, useEffect } from 'react';
import {
    Container, Row, Col, Card, Table, Badge, Alert,
    Button, Modal, Form, ListGroup, Accordion
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';

// Mock API Service for View Details
const viewService = {
    getRequestDetails: async (id) => {
        // Mock data matching your backend structure
        return {
            ID: parseInt(id),
            REQ_DATE: '2024-01-15T10:30:00',
            EMP_ID: 101,
            CUS_CODE: 'CUST001',
            CUS_NAME: 'John Doe',
            CUS_ADDRESS: '123 Main St, City',
            ITEM_CODE: 'PROD001',
            ITEM_NAME: 'AC Unit Pro',
            MODEL: 'AC-PRO-2024',
            SERIAL_NO: 'SN-AC001-2024',
            INSTALLATION_DATE: '2023-12-15',
            NOTE: 'AC not cooling properly, needs service',
            REQ_STATUS: 'PENDING_APPROVAL', // PENDING_APPROVAL, APPROVED, REJECTED, COMPLETED
            TOTAL_AMOUNT: 164.98,
            REQ_FILES: ['photo1.jpg', 'service_report.pdf'],
            PO_REFERENCE: 'PO-2024-001',
            MS_SERVICE_REQUEST_ITEM: [
                {
                    ID: 1,
                    ITEM_CODE: 'SPARE001',
                    ITEM_DESC: 'Fan Motor 1200 RPM',
                    UNIT_PRICE: 89.99,
                    QTY: 1,
                    AMOUNT: 89.99
                },
                {
                    ID: 2,
                    ITEM_CODE: 'SPARE002',
                    ITEM_DESC: 'AC Filter Premium',
                    UNIT_PRICE: 24.99,
                    QTY: 3,
                    AMOUNT: 74.99
                }
            ],
            APPROVALS: [
                {
                    ID: 1,
                    ACTION_BY: 'Manager 1',
                    ACTION_DATE: '2024-01-16T09:15:00',
                    ACTION: 'APPROVED',
                    COMMENTS: 'Looks good, proceed with service',
                    ROLE: 'Technical Manager'
                },
                {
                    ID: 2,
                    ACTION_BY: 'Finance Officer',
                    ACTION_DATE: '2024-01-16T14:30:00',
                    ACTION: 'PENDING',
                    COMMENTS: 'Waiting for budget approval',
                    ROLE: 'Finance'
                },
                {
                    ID: 3,
                    ACTION_BY: 'Service Head',
                    ACTION_DATE: null,
                    ACTION: 'PENDING',
                    COMMENTS: '',
                    ROLE: 'Service Department'
                },
                {
                    ID: 4,
                    ACTION_BY: 'Final Approver',
                    ACTION_DATE: null,
                    ACTION: 'PENDING',
                    COMMENTS: '',
                    ROLE: 'Final Approval'
                }
            ]
        };
    },

    getApproversList: async () => {
        return [
            { id: 1, name: 'Manager 1', role: 'Technical Manager', email: 'manager1@company.com' },
            { id: 2, name: 'Finance Officer', role: 'Finance', email: 'finance@company.com' },
            { id: 3, name: 'Service Head', role: 'Service Department', email: 'service@company.com' },
            { id: 4, name: 'Final Approver', role: 'Final Approval', email: 'final@company.com' }
        ];
    },

    submitApproval: async (requestId, approvalData) => {
        console.log('Submitting approval:', { requestId, approvalData });
        return { success: true };
    }
};

// Service type mapping
const SERVICE_TYPES = {
    DEMO: { label: 'Demo Request', color: 'primary' },
    WARRANTY: { label: 'Warranty Request', color: 'success' },
    SERVICE: { label: 'Service Invoice', color: 'info' }
};

// Status mapping with colors
const STATUS_CONFIG = {
    DRAFT: { label: 'Draft', color: 'secondary' },
    PENDING_APPROVAL: { label: 'Pending Approval', color: 'warning' },
    APPROVED: { label: 'Approved', color: 'success' },
    REJECTED: { label: 'Rejected', color: 'danger' },
    COMPLETED: { label: 'Completed', color: 'info' },
    IN_PROGRESS: { label: 'In Progress', color: 'primary' }
};

// Approval Action Validation Schema
const approvalSchema = Yup.object().shape({
    ACTION: Yup.string().required('Action is required'),
    COMMENTS: Yup.string().when('ACTION', {
        is: 'REJECTED',
        then: Yup.string().required('Comments are required for rejection'),
        otherwise: Yup.string()
    })
});

// Approver Action Modal
const ApproverActionModal = ({ show, onHide, onSubmit, requestId, currentApprover }) => {
    const initialValues = {
        ACTION: '',
        COMMENTS: ''
    };

    const handleSubmit = (values, { setSubmitting }) => {
        const approvalData = {
            ...values,
            ACTION_BY: currentApprover?.name || 'Current User',
            ROLE: currentApprover?.role || 'Approver',
            REQ_ID: requestId
        };

        onSubmit(approvalData);
        setSubmitting(false);
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Take Action</Modal.Title>
            </Modal.Header>
            <Formik
                initialValues={initialValues}
                validationSchema={approvalSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, handleSubmit, isSubmitting, setFieldValue }) => (
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Action *</Form.Label>
                                <div>
                                    <Form.Check
                                        inline
                                        type="radio"
                                        label="Approve"
                                        name="ACTION"
                                        id="APPROVE"
                                        value="APPROVED"
                                        checked={values.ACTION === 'APPROVED'}
                                        onChange={() => setFieldValue('ACTION', 'APPROVED')}
                                    />
                                    <Form.Check
                                        inline
                                        type="radio"
                                        label="Reject"
                                        name="ACTION"
                                        id="REJECT"
                                        value="REJECTED"
                                        checked={values.ACTION === 'REJECTED'}
                                        onChange={() => setFieldValue('ACTION', 'REJECTED')}
                                    />
                                    <Form.Check
                                        inline
                                        type="radio"
                                        label="Request Changes"
                                        name="ACTION"
                                        id="REQUEST_CHANGES"
                                        value="REQUEST_CHANGES"
                                        checked={values.ACTION === 'REQUEST_CHANGES'}
                                        onChange={() => setFieldValue('ACTION', 'REQUEST_CHANGES')}
                                    />
                                </div>
                                {errors.ACTION && touched.ACTION && (
                                    <Form.Text className="text-danger">{errors.ACTION}</Form.Text>
                                )}
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Comments</Form.Label>
                                <Field
                                    as={Form.Control}
                                    name="COMMENTS"
                                    as="textarea"
                                    rows={3}
                                    placeholder="Enter your comments..."
                                />
                                {errors.COMMENTS && touched.COMMENTS && (
                                    <Form.Text className="text-danger">{errors.COMMENTS}</Form.Text>
                                )}
                            </Form.Group>

                            {currentApprover && (
                                <Alert variant="info">
                                    <strong>Approving as:</strong> {currentApprover.name} ({currentApprover.role})
                                </Alert>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={onHide}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Submitting...' : 'Submit Action'}
                            </Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

// Main View Details Component
const ServiceRequestApprovalPage = ({ isApproverView = false }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [request, setRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showActionModal, setShowActionModal] = useState(false);
    const [currentApprover, setCurrentApprover] = useState(null);
    const [approvers, setApprovers] = useState([]);

    useEffect(() => {
        loadRequestDetails();
        loadApprovers();
    }, [id]);

    const loadRequestDetails = async () => {
        try {
            const data = await viewService.getRequestDetails(id);
            setRequest(data);

            // Determine service type from status or other fields
            if (data.SERIAL_NO) {
                data.serviceType = 'WARRANTY';
            } else if (data.PO_REFERENCE) {
                data.serviceType = 'SERVICE';
            } else {
                data.serviceType = 'DEMO';
            }
        } catch (err) {
            setError('Failed to load request details');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadApprovers = async () => {
        try {
            const data = await viewService.getApproversList();
            setApprovers(data);

            // Set current approver (in real app, this would be based on logged-in user)
            if (isApproverView && data.length > 0) {
                setCurrentApprover(data[1]); // Example: second approver
            }
        } catch (err) {
            console.error('Failed to load approvers:', err);
        }
    };

    const handleApprovalSubmit = async (approvalData) => {
        try {
            await viewService.submitApproval(id, approvalData);
            alert('Action submitted successfully!');
            loadRequestDetails(); // Refresh data
        } catch (err) {
            alert('Failed to submit action');
            console.error(err);
        }
    };

    const getStatusBadge = (status) => {
        const config = STATUS_CONFIG[status] || { label: status, color: 'secondary' };
        return <Badge bg={config.color}>{config.label}</Badge>;
    };

    const getActionBadge = (action) => {
        const colors = {
            APPROVED: 'success',
            REJECTED: 'danger',
            PENDING: 'warning',
            REQUEST_CHANGES: 'info'
        };
        return <Badge bg={colors[action] || 'secondary'}>{action}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not yet';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <Container className="mt-4 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading request details...</p>
            </Container>
        );
    }

    if (error || !request) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">
                    {error || 'Request not found'}
                </Alert>
                <Button variant="outline-primary" onClick={() => navigate(-1)}>
                    Go Back
                </Button>
            </Container>
        );
    }

    return (
        <Container fluid className="py-4">
            {/* Header Section */}
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <h2>Service Request Details</h2>
                            <div className="d-flex align-items-center gap-3 mt-2">
                                <Badge bg={SERVICE_TYPES[request.serviceType]?.color || 'primary'}>
                                    {SERVICE_TYPES[request.serviceType]?.label}
                                </Badge>
                                {getStatusBadge(request.REQ_STATUS)}
                                <span className="text-muted">ID: {request.ID}</span>
                            </div>
                        </div>
                        <div>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)} className="me-2">
                                Back to List
                            </Button>
                            {isApproverView && (
                                <Button
                                    variant="primary"
                                    onClick={() => setShowActionModal(true)}
                                    disabled={request.REQ_STATUS === 'COMPLETED'}
                                >
                                    Take Action
                                </Button>
                            )}
                        </div>
                    </div>
                    <hr />
                </Col>
            </Row>

            {/* Basic Details Card */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <h5 className="mb-0">Request Information</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <ListGroup variant="flush">
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>Request Date:</strong> {formatDate(request.REQ_DATE)}
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>Employee ID:</strong> {request.EMP_ID || 'N/A'}
                                </ListGroup.Item>
                                {request.SERIAL_NO && (
                                    <ListGroup.Item className="border-0 px-0">
                                        <strong>Serial Number:</strong> {request.SERIAL_NO}
                                    </ListGroup.Item>
                                )}
                                {request.PO_REFERENCE && (
                                    <ListGroup.Item className="border-0 px-0">
                                        <strong>PO Reference:</strong> {request.PO_REFERENCE}
                                    </ListGroup.Item>
                                )}
                                {request.INSTALLATION_DATE && (
                                    <ListGroup.Item className="border-0 px-0">
                                        <strong>Installation Date:</strong> {new Date(request.INSTALLATION_DATE).toLocaleDateString()}
                                    </ListGroup.Item>
                                )}
                            </ListGroup>
                        </Col>
                        <Col md={6}>
                            <ListGroup variant="flush">
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>Customer:</strong> {request.CUS_NAME} ({request.CUS_CODE})
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>Address:</strong> {request.CUS_ADDRESS}
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>Product:</strong> {request.ITEM_NAME} ({request.ITEM_CODE})
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 px-0">
                                    <strong>Model:</strong> {request.MODEL}
                                </ListGroup.Item>
                            </ListGroup>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Description/Notes Card */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <h5 className="mb-0">
                        {request.serviceType === 'SERVICE' ? 'Work Done Details' : 'Complaint Details'}
                    </h5>
                </Card.Header>
                <Card.Body>
                    <p className="mb-0">{request.NOTE}</p>
                </Card.Body>
            </Card>

            {/* Spare Items Section (if exists) */}
            {request.MS_SERVICE_REQUEST_ITEM && request.MS_SERVICE_REQUEST_ITEM.length > 0 && (
                <Card className="mb-4">
                    <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Spare Items</h5>
                        <Badge bg="info" pill>
                            Total: ${request.TOTAL_AMOUNT?.toFixed(2)}
                        </Badge>
                    </Card.Header>
                    <Card.Body>
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead className="table-light">
                                    <tr>
                                        <th>SL No</th>
                                        <th>Item Code</th>
                                        <th>Description</th>
                                        <th className="text-end">Unit Price</th>
                                        <th className="text-center">Quantity</th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {request.MS_SERVICE_REQUEST_ITEM.map((item, index) => (
                                        <tr key={item.ID}>
                                            <td>{index + 1}</td>
                                            <td>{item.ITEM_CODE}</td>
                                            <td>{item.ITEM_DESC}</td>
                                            <td className="text-end">${item.UNIT_PRICE?.toFixed(2)}</td>
                                            <td className="text-center">{item.QTY}</td>
                                            <td className="text-end">${item.AMOUNT?.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="table-active">
                                        <td colSpan={5} className="text-end fw-bold">Total Amount:</td>
                                        <td className="text-end fw-bold">
                                            ${request.TOTAL_AMOUNT?.toFixed(2)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Attachments Section */}
            {request.REQ_FILES && request.REQ_FILES.length > 0 && (
                <Card className="mb-4">
                    <Card.Header className="bg-light">
                        <h5 className="mb-0">Attachments</h5>
                    </Card.Header>
                    <Card.Body>
                        <ListGroup>
                            {request.REQ_FILES.map((file, index) => (
                                <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                                    <span>
                                        <i className="bi bi-file-earmark me-2"></i>
                                        {file}
                                    </span>
                                    <Button variant="outline-primary" size="sm">
                                        Download
                                    </Button>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </Card.Body>
                </Card>
            )}

            {/* Approval Workflow Section */}
            <Card className="mb-4">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Approval Workflow</h5>
                    <Badge bg="secondary">
                        {request.APPROVALS?.filter(a => a.ACTION === 'APPROVED').length || 0} / {request.APPROVALS?.length || 0} Approved
                    </Badge>
                </Card.Header>
                <Card.Body>
                    {request.APPROVALS && request.APPROVALS.length > 0 ? (
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead className="table-light">
                                    <tr>
                                        <th width="10%">Step</th>
                                        <th width="20%">Role</th>
                                        <th width="20%">Action By</th>
                                        <th width="15%">Status</th>
                                        <th width="20%">Date & Time</th>
                                        <th width="25%">Comments</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {request.APPROVALS.map((approval, index) => (
                                        <tr key={approval.ID} className={approval.ACTION === 'PENDING' ? 'table-warning' : ''}>
                                            <td className="text-center">{index + 1}</td>
                                            <td>
                                                <strong>{approval.ROLE}</strong>
                                            </td>
                                            <td>
                                                {approval.ACTION_BY || (
                                                    <span className="text-muted">Waiting for action</span>
                                                )}
                                            </td>
                                            <td>{getActionBadge(approval.ACTION)}</td>
                                            <td>{formatDate(approval.ACTION_DATE)}</td>
                                            <td>
                                                {approval.COMMENTS ? (
                                                    <div className="text-truncate" title={approval.COMMENTS}>
                                                        {approval.COMMENTS}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">No comments</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <Alert variant="info" className="mb-0">
                            No approval workflow defined for this request type.
                        </Alert>
                    )}

                    {/* Approver Action (for approver view) */}
                    {isApproverView && currentApprover && (
                        <Accordion className="mt-4">
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>
                                    <strong>Take Action as {currentApprover.role}</strong>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <Alert variant="info" className="mb-3">
                                        You are approving as <strong>{currentApprover.name}</strong> ({currentApprover.role})
                                    </Alert>

                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="success"
                                            onClick={() => setShowActionModal(true)}
                                        >
                                            <i className="bi bi-check-circle me-2"></i>
                                            Approve
                                        </Button>
                                        <Button
                                            variant="danger"
                                            onClick={() => {
                                                //setFieldValue('ACTION', 'REJECTED');
                                                setShowActionModal(true);
                                            }}
                                        >
                                            <i className="bi bi-x-circle me-2"></i>
                                            Reject
                                        </Button>
                                        <Button
                                            variant="warning"
                                            onClick={() => {
                                               // setFieldValue('ACTION', 'REQUEST_CHANGES');
                                                setShowActionModal(true);
                                            }}
                                        >
                                            <i className="bi bi-arrow-clockwise me-2"></i>
                                            Request Changes
                                        </Button>
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    )}
                </Card.Body>
            </Card>

            {/* Action History Timeline (Alternative View) */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <h5 className="mb-0">Approval Timeline</h5>
                </Card.Header>
                <Card.Body>
                    <div className="timeline">
                        {request.APPROVALS && request.APPROVALS.map((approval, index) => (
                            <div key={approval.ID} className="timeline-item">
                                <div className="timeline-marker">
                                    <div className={`marker ${approval.ACTION === 'APPROVED' ? 'bg-success' :
                                        approval.ACTION === 'REJECTED' ? 'bg-danger' :
                                            approval.ACTION === 'PENDING' ? 'bg-warning' : 'bg-secondary'}`}></div>
                                </div>
                                <div className="timeline-content">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <strong>{approval.ROLE}</strong>
                                            {approval.ACTION_BY && (
                                                <span className="text-muted ms-2">by {approval.ACTION_BY}</span>
                                            )}
                                        </div>
                                        <div>
                                            <Badge bg={approval.ACTION === 'APPROVED' ? 'success' :
                                                approval.ACTION === 'REJECTED' ? 'danger' : 'warning'}>
                                                {approval.ACTION}
                                            </Badge>
                                        </div>
                                    </div>
                                    {approval.ACTION_DATE && (
                                        <div className="text-muted small">
                                            {formatDate(approval.ACTION_DATE)}
                                        </div>
                                    )}
                                    {approval.COMMENTS && (
                                        <div className="mt-2 p-2 bg-light rounded">
                                            {approval.COMMENTS}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card.Body>
            </Card>

            {/* Approver Action Modal */}
            <ApproverActionModal
                show={showActionModal}
                onHide={() => setShowActionModal(false)}
                onSubmit={handleApprovalSubmit}
                requestId={request.ID}
                currentApprover={currentApprover}
            />

            <style jsx>{`
        .timeline {
          position: relative;
          padding-left: 30px;
        }
        
        .timeline-item {
          position: relative;
          margin-bottom: 20px;
        }
        
        .timeline-marker {
          position: absolute;
          left: -30px;
          top: 0;
        }
        
        .timeline-marker .marker {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 0 0 3px #dee2e6;
        }
        
        .timeline-content {
          padding: 10px;
          background: white;
          border-radius: 4px;
        }
        
        .timeline-item:not(:last-child) .timeline-content:after {
          content: '';
          position: absolute;
          left: -20px;
          top: 20px;
          width: 2px;
          height: calc(100% + 20px);
          background: #dee2e6;
        }
      `}</style>
        </Container>
    );
};

export default ServiceRequestApprovalPage;